import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { AlertCircleIcon, CheckCircleIcon, ClockIcon } from 'lucide-react-native';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Keyboard, Linking } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';

import { AwarenessLogo } from '@/components/AwarenessLogo';
import { NpiConflictInfoBox } from '@/components/NpiConflictInfoBox';
import { ConfirmModal } from '@/components/confirmModal';
import { SupportForm } from '@/components/supportForm';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField, InputSlot } from '@/components/ui/input';
import { InputAccessoryView } from '@/components/ui/input-accessory-view';
import { Link, LinkText } from '@/components/ui/link';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@/constants/links';
import { ROUTES } from '@/constants/routes';
import { NpiSuggestionPicker, type NpiCandidate } from '@/features/npi-suggestions';
import { useColors } from '@/hooks/useColors';
import { useCreateAccount } from '@/hooks/useCreateAccount';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { useToaster } from '@/hooks/useToaster';
import { saveAccountLinkingContext } from '@/services/accountLinking';
import {
  signInWithGoogle,
  fetchAuthSession,
  getIdToken,
  signOutLocal,
  signOut,
} from '@/services/auth';
import {
  completeOAuthSignup,
  type CompleteOAuthSignupRequest,
} from '@/services/completeOAuthSignup';
import {
  getCurrentUserV2,
  getCurrentUserV2QueryOptions,
  refreshCurrentUserV2,
} from '@/services/getCurrentUserV2';
import { getProviderV2, getProviderV2QueryOptions } from '@/services/getProviderV2';
import { authenticatedUserIdAtom, userIdAtom } from '@/services/globalState';
import { isOAuthUser, parseIdTokenClaims } from '@/services/session';
import { setNpi } from '@/services/setNpi';
import {
  trackNpiVerificationFailed,
  trackNpiVerificationViewed,
  trackNpiReviewPendingViewed,
  trackNpiVerificationCompleted,
  trackNpiVerificationSkipClicked,
  trackNpiVerificationSkipConfirmed,
  trackNpiVerificationSkipCancelled,
  trackSignupNpiValidationFailed,
  trackNpiValidated,
  trackNpiRegistryClicked,
  trackSupportModalOpened,
  trackAccountCreated,
  trackAccountConflictDetected,
  trackAccountLinkingInitiated,
} from '@/util/analyticsEvents';
import { isPendingOAuthUserId } from '@/util/authTokens';
import { isApiErrorV2 } from '@/util/fetchClient';
import { trackGoogleAdsAccountCreatedVerifiedConversion } from '@/util/googleAdsConversion';
import { getPlatformStorage } from '@/util/platformStorage';
import { Sentry } from '@/util/sentry';

/**
 * NPI conflict resolution data from API error responses
 */
interface NpiConflictResolution {
  duplicateUserId: number;
  maskedEmail: string;
  authProvider: 'google' | 'password';
}

const formSchema = z.object({
  name: z.string().optional(),
  npiNumber: z
    .string()
    .min(1, 'NPI number is required')
    .length(10, 'NPI number must be 10 digits')
    .regex(/^\d+$/, 'NPI number must be 10 digits'),
});

async function readReferralCodeFromLocalStorage(): Promise<string | null> {
  try {
    const storage = getPlatformStorage(undefined, 'local');

    return await storage.getItem('primum_referral_code');
  } catch {
    return null;
  }
}

type ProviderProfile = NonNullable<Awaited<ReturnType<typeof getProviderV2>>>;

/** A whole NPI has been typed or filled in, so there is nothing left to look up for the person. */
/**
 * The public NPPES search, offered beside our own so a person is never stuck
 * when our matches miss them or our search is down.
 */
const NPI_REGISTRY_URL = 'https://npiregistry.cms.hhs.gov/search';

function isCompleteNpi(npi: string): boolean {
  return /^\d{10}$/.test(npi);
}

function npiInputProviderStatusSlot(options: {
  isLoadingProvider: boolean;
  isFetchingProvider: boolean;
  provider: ProviderProfile | null | undefined;
  debouncedNpi: string;
}): ReactNode {
  const { isLoadingProvider, isFetchingProvider, provider, debouncedNpi } = options;

  if (isLoadingProvider) {
    return (
      <InputSlot className="pr-2">
        <Spinner size="small" />
      </InputSlot>
    );
  }

  if (!isFetchingProvider && provider && debouncedNpi.length === 10) {
    return (
      <InputSlot className="pr-2">
        <Icon as={CheckCircleIcon} className="text-success-500" />
      </InputSlot>
    );
  }

  return null;
}

/**
 * Blocking status card shown in place of the NPI form once an already-submitted NPI is held
 * for review (`currentUser.npiConfirmationHeldForReview`). Deliberately has no input and no
 * submit action — the user has nothing left to enter, so re-showing the form would just invite
 * a pointless resubmission of the same NPI. Copy stays neutral about *why* it's held (the
 * geo-fraud gate reason isn't ours to disclose to the user).
 */
function NpiHeldForReviewCard({ onContactSupport }: { onContactSupport: () => void }): ReactNode {
  return (
    <VStack
      className="shadow-soft mt-3 items-start gap-4 self-stretch rounded-lg bg-background-0 px-6 pb-10 pt-6 web:mt-6 web:py-10"
      style={{ boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.1)' }}>
      <HStack className="items-center gap-2 self-stretch">
        <Icon as={ClockIcon} className="h-6 w-6 text-primary-500" />
        <Text size="xl" className="font-normal text-typography-900">
          Confirming your credentials
        </Text>
      </HStack>
      <Text size="md" className="self-stretch text-left font-normal text-typography-900">
        Thanks for submitting your NPI. Our team is completing a quick confirmation and will let you
        know once it's done — no need to resubmit it.
      </Text>
      <Text size="sm" className="self-stretch text-left text-typography-700">
        Need help sooner?{' '}
        <LinkText
          onPress={onContactSupport}
          size="sm"
          className="text-primary-500 underline data-[hover=true]:text-primary-600">
          Contact Support
        </LinkText>
      </Text>
    </VStack>
  );
}

/**
 * NPI Verification
 * Responsibilities:
 * - Validate name and 10-digit NPI; debounce lookup; prevent render loops
 * - Update user and cache; navigate to /directory on success
 * - Track view/completion/failure and support modal opens
 */
export type NpiVerificationProps = {
  inModal?: boolean;
};

/**
 * NPI verification component for completing user registration.
 *
 * Handles both pending OAuth signup (no DB user yet) and existing user NPI updates.
 * Manages NPI conflict resolution with account linking flow. Prevents form submission
 * when conflicts are active.
 *
 * ACCOUNT LINKING FLOW:
 * For password provider conflicts: Navigates to login page before signing out to prevent
 * landing page flash. User signs in with password to complete linking.
 *
 * For Google provider conflicts: Uses LOGIN prompt with loginHint to ensure the same
 * Google account is used that triggered the conflict. This prevents account switching
 * during the linking process.
 *
 * Important behaviors:
 * - After completing OAuth signup, optimistically updates the `currentUserV2` cache with the
 *   newly created user and then invalidates the query to refetch in the background. This avoids
 *   a race where the secure layout could momentarily see stale user data and redirect.
 */
/** The login screen named in its own group, for a redirect leaving the secure group. */
const OPEN_LOGIN_PATH = '/(open)/login';

export default function NpiVerification({ inModal = false }: NpiVerificationProps) {
  const { colors } = useColors();

  const { bottom, top } = useSafeAreaInsets();

  const { replace } = useRouter();

  const { referred_by: referredByParam } = useLocalSearchParams<{ referred_by?: string }>();

  const toast = useToaster();

  const { appendRedirectPath, navigateToRedirectPath } = useRedirectPath();

  const redirectAfterVerification = async () => {
    navigateToRedirectPath(ROUTES.DIRECTORY_TAB as string);
  };

  const userId = useAtomValue(userIdAtom);

  const { data: createAccountData } = useCreateAccount();

  const setAuthenticatedUserId = useSetAtom(authenticatedUserIdAtom);

  const [isPendingOAuthSignup, setIsPendingOAuthSignup] = useState(false);

  const { data: currentUser } = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    enabled: !isPendingOAuthUserId(userId),
  });

  const npiHeldForReview = currentUser?.npiConfirmationHeldForReview === true;

  const [showSupportModal, setShowSupportModal] = useState(false);

  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  const [showNpiConflict, setShowNpiConflict] = useState(false);

  const [pendingUserData, setPendingUserData] = useState<{
    email?: string;
    firstName?: string;
    lastName?: string;
  }>({});

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim(),
      npiNumber: currentUser?.npiNumber || '',
    },
    resolver: zodResolver(formSchema),
  });

  const [registrySearchRequested, setRegistrySearchRequested] = useState(false);

  /**
   * A confirmed suggestion fills the NPI field, so it goes through the same
   * lookup, conflict check and submit as a typed number. The screen asks for
   * no name: the server takes the account's name from the NPI record, and
   * the chosen one rides along for analytics.
   */
  const fillFromSuggestion = (candidate: NpiCandidate) => {
    setValue('npiNumber', candidate.npi, { shouldDirty: true });

    setValue('name', `${candidate.firstName} ${candidate.lastName}`, { shouldDirty: true });

    setRegistrySearchRequested(false);
  };

  /**
   * "Not you?" under a looked-up NPI: empties the field, which brings the
   * matches back, so a wrong pick or a mistyped number is one tap to undo.
   */
  const clearNpi = () => {
    setValue('npiNumber', '', { shouldDirty: true });

    setValue('name', '', { shouldDirty: true });

    clearErrors('npiNumber');
  };

  const { mutateAsync: setNpiMutation, isPending } = useMutation({
    mutationFn: setNpi,
  });

  const queryClient = useQueryClient();

  const npiNumber = watch('npiNumber') || '';

  const [debouncedNpi] = useDebounce(npiNumber, 400);

  /**
   * A request to search by name answers the NPI in the field when it was made.
   * Any edit to the field, typed, chosen or cleared, withdraws it.
   */
  useEffect(() => {
    setRegistrySearchRequested(false);
  }, [npiNumber]);

  /**
   * The matches lead the screen, with typing an NPI as the way out beneath
   * them. They step aside once a whole NPI is in the field, typed or chosen,
   * unless the person asked to search by name again.
   */
  const suggestionsShown =
    !npiHeldForReview && (!isCompleteNpi(npiNumber) || registrySearchRequested);

  const {
    data: provider,
    isFetching: isFetchingProvider,
    isLoading: isLoadingProvider,
    error: providerError,
  } = useQuery({
    ...getProviderV2QueryOptions(debouncedNpi),
    enabled: isCompleteNpi(debouncedNpi) && !npiHeldForReview,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const isTenDigits = isCompleteNpi(debouncedNpi);

  const hasInvalidNpiErrorRef = useRef(false);

  const conflictResolutionRef = useRef<null | NpiConflictResolution>(null);

  const providerStatusSlot = npiInputProviderStatusSlot({
    isLoadingProvider,
    isFetchingProvider,
    provider,
    debouncedNpi: debouncedNpi ?? '',
  });

  const npiErrorMessage = errors.npiNumber?.message?.toString();

  const showSupportLinkForNpiError =
    npiErrorMessage === 'This NPI number is invalid' ||
    npiErrorMessage === 'This NPI number is already in use';

  useEffect(() => {
    const checkPendingStatus = async () => {
      try {
        const idToken = await getIdToken();

        if (!idToken) {
          return;
        }

        const claims = parseIdTokenClaims(idToken);

        if (!claims) {
          return;
        }

        const isPending = isOAuthUser(claims) && !claims['custom:user_id'];

        if (isPending) {
          setIsPendingOAuthSignup(true);

          const pendingData = {
            email: claims.email,
            firstName: claims.given_name || claims.name?.split(' ')[0] || '',
            lastName: claims.family_name || claims.name?.split(' ').slice(1).join(' ') || '',
          };

          setPendingUserData(pendingData);
        }
      } catch {}
    };

    checkPendingStatus();
  }, []);

  useEffect(() => {
    if (!isTenDigits) {
      if (hasInvalidNpiErrorRef.current) {
        clearErrors('npiNumber');

        hasInvalidNpiErrorRef.current = false;
      }

      conflictResolutionRef.current = null;

      setShowNpiConflict(false);

      return;
    }

    if (providerError) {
      if (!hasInvalidNpiErrorRef.current) {
        if (isApiErrorV2(providerError)) {
          const body = providerError.body as {
            error?: string;
            errorCode?: 'NPI_IN_USE' | 'EMAIL_IN_USE' | string;
            resolution?: NpiConflictResolution;
          };

          const isConflict = providerError.status === 409;

          const hasResolution = Boolean(
            body?.resolution?.duplicateUserId &&
            body?.resolution?.maskedEmail &&
            body?.resolution?.authProvider
          );

          if (isConflict && hasResolution) {
            const authProvider = body.resolution!.authProvider === 'google' ? 'google' : 'password';

            conflictResolutionRef.current = {
              duplicateUserId: Number(body.resolution!.duplicateUserId),
              maskedEmail: String(body.resolution!.maskedEmail),
              authProvider,
            };

            trackAccountConflictDetected({
              conflictType: body.errorCode === 'EMAIL_IN_USE' ? 'EMAIL_IN_USE' : 'NPI_IN_USE',
              authProvider,
              signupMethod: isPendingOAuthSignup ? 'oauth' : 'password',
              source: 'npi_verification',
              userId,
              email: pendingUserData.email || currentUser?.email,
            });

            if (isPendingOAuthSignup) {
              setShowNpiConflict(true);
            } else {
              setShowNpiConflict(false);
            }
          } else {
            conflictResolutionRef.current = null;

            setShowNpiConflict(false);
          }

          const isAlreadyInUse =
            (!!hasResolution && isConflict) ||
            (body.error || '').toLowerCase().includes('already associated');

          setError('npiNumber', {
            message: isAlreadyInUse
              ? 'This NPI number is already in use'
              : 'This NPI number is invalid',
          });
        } else {
          setError('npiNumber', { message: 'This NPI number is invalid' });

          conflictResolutionRef.current = null;

          setShowNpiConflict(false);
        }

        hasInvalidNpiErrorRef.current = true;
      }
    } else if (hasInvalidNpiErrorRef.current) {
      clearErrors('npiNumber');

      hasInvalidNpiErrorRef.current = false;

      conflictResolutionRef.current = null;

      setShowNpiConflict(false);
    }
  }, [providerError, isTenDigits, setError, clearErrors]);

  const lastInvalidToastForNpiRef = useRef<string | null>(null);

  const lastValidatedNpiRef = useRef<string | null>(null);

  const lastValidationFailedNpiRef = useRef<string | null>(null);

  useEffect(() => {
    if (isTenDigits && providerError && debouncedNpi !== lastInvalidToastForNpiRef.current) {
      lastInvalidToastForNpiRef.current = debouncedNpi;

      if (isApiErrorV2(providerError)) {
        const body = providerError.body as {
          error?: string;
          errorCode?: 'NPI_IN_USE' | 'EMAIL_IN_USE' | string;
          resolution?: NpiConflictResolution;
        };

        const hasResolution =
          (body.errorCode === 'NPI_IN_USE' || body.errorCode === 'EMAIL_IN_USE') &&
          body.resolution?.duplicateUserId;

        if (!hasResolution) {
          const isAlreadyInUse = (body.error || '').toLowerCase().includes('already associated');

          toast.showError({
            title: isAlreadyInUse
              ? 'This NPI number is already in use'
              : 'This NPI number is invalid',
          });
        }
      } else {
        toast.showError({ title: 'This NPI number is invalid' });
      }
    }

    if (!isTenDigits) {
      lastInvalidToastForNpiRef.current = null;
    }
  }, [isTenDigits, providerError, debouncedNpi, toast]);

  useEffect(() => {
    if (!isTenDigits) {
      lastValidatedNpiRef.current = null;

      lastValidationFailedNpiRef.current = null;

      return;
    }

    if (provider && debouncedNpi !== lastValidatedNpiRef.current) {
      trackNpiValidated({ npiNumber: debouncedNpi });

      lastValidatedNpiRef.current = debouncedNpi;

      return;
    }

    if (providerError && debouncedNpi !== lastValidationFailedNpiRef.current) {
      trackSignupNpiValidationFailed({ npiNumber: debouncedNpi, error: providerError });

      lastValidationFailedNpiRef.current = debouncedNpi;
    }
  }, [isTenDigits, provider, providerError, debouncedNpi]);

  useEffect(() => {
    trackNpiVerificationViewed();
  }, []);

  useEffect(() => {
    if (npiHeldForReview) {
      trackNpiReviewPendingViewed();
    }
  }, [npiHeldForReview]);

  /**
   * Handles NPI verification form submission.
   *
   * For pending OAuth signups:
   * - Checks for NPI conflicts and shows inline info box if found
   * - Completes OAuth signup with NPI if no conflicts
   *
   * For existing users:
   * - Checks for NPI conflicts and shows inline info box if found
   * - Updates user's NPI if no conflicts
   *
   * Error handling:
   * - Shows toast only for invalid NPIs, not conflicts (conflicts handled by info box)
   * - Sets conflict state to display inline resolution UI
   */
  const handleNpiVerification = handleSubmit(async (values) => {
    try {
      if (conflictResolutionRef.current) {
        setError('npiNumber', { message: 'This NPI number is already in use' });

        if (isPendingOAuthSignup) {
          toast.showError({
            title: 'This NPI number is already in use',
            message: 'Please sign in to your existing account to link.',
          });
        }

        return;
      }

      if (!provider) {
        setError('npiNumber', { message: 'This NPI number is invalid' });

        toast.showError({ title: 'This NPI number is invalid' });

        return;
      }

      if (isPendingOAuthSignup) {
        // Get referral code from atom (email/password flow) or platform storage (OAuth flow)
        const referralCode =
          createAccountData?.referredBy || (await readReferralCodeFromLocalStorage());

        const requestBody = {
          email: pendingUserData.email!,
          firstName: provider.firstName,
          state: provider.state,
          npiNumber: values.npiNumber,
          ...(values.name ? { name: values.name.trim() } : {}),
          ...(referralCode ? { referredBy: referralCode, referralCode } : {}),
        } satisfies CompleteOAuthSignupRequest;

        const oAuthSignupResult = await completeOAuthSignup(requestBody);

        // Always clear referral code after successful signup (OAuth flow)
        try {
          const storage = getPlatformStorage(undefined, 'local');

          await storage.removeItem('primum_referral_code');
        } catch {}

        trackNpiVerificationCompleted({
          name: values.name?.trim() || '',
          npiNumber: values.npiNumber,
        });

        const resolveNewUserAfterOAuthSignup = async (): Promise<{
          id?: number;
          userType?: string;
        }> => {
          try {
            const idToken = await getIdToken();

            if (!idToken) {
              return {};
            }

            const currentUser = await getCurrentUserV2({
              Authorization: `Bearer ${idToken}`,
            });

            const uid = currentUser?.id;

            if (uid) {
              setAuthenticatedUserId(String(uid));

              queryClient.setQueryData(['currentUserV2', String(uid)], currentUser);

              await queryClient.invalidateQueries({
                queryKey: ['currentUserV2', String(uid)],
              });
            }

            return { id: uid, userType: currentUser?.userType };
          } catch (err: unknown) {
            const status = isApiErrorV2(err) ? err.status : undefined;

            const message = err instanceof Error ? err.message : String(err);

            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Post-signup user ID resolution failed',
              level: 'warning',
              data: {
                status,
                error: message,
              },
            });

            return {};
          }
        };

        const newUser = await resolveNewUserAfterOAuthSignup();

        trackAccountCreated({
          email: pendingUserData.email!,
          ...(newUser.id ? { userId: String(newUser.id) } : {}),
          firstName: provider.firstName,
          lastName: provider.lastName,
          userType: newUser.userType,
          npiNumber: values.npiNumber,
          state: provider.state,
          referredByName: oAuthSignupResult?.referredByName || referralCode || undefined,
          referredBy: referralCode || undefined,
        });

        trackGoogleAdsAccountCreatedVerifiedConversion();

        toast.showSuccess({ title: 'Your NPI is verified', message: "You're all set." });

        await redirectAfterVerification();

        return;
      }

      const setNpiResult = await setNpiMutation({
        npiNumber: values.npiNumber,
        name: (values.name || '').trim(),
        firstName: provider.firstName,
        lastName: provider.lastName,
        state: provider.state,
      });

      if (userId) {
        await refreshCurrentUserV2(queryClient, userId, 'NpiVerification');
      }

      trackNpiVerificationCompleted({ name: values.name.trim(), npiNumber: values.npiNumber });

      const referredByNameFromApi = setNpiResult?.referredByName;

      const referralCodeFromAtomOrParam = createAccountData?.referredBy || referredByParam || null;

      const referralCode = referredByNameFromApi
        ? referralCodeFromAtomOrParam
        : referralCodeFromAtomOrParam || (await readReferralCodeFromLocalStorage());

      const referredByName = referredByNameFromApi || referralCode || undefined;

      trackAccountCreated({
        email: currentUser?.email || '',
        userId,
        firstName: provider.firstName,
        lastName: provider.lastName,
        userType: currentUser?.userType,
        npiNumber: values.npiNumber,
        state: provider.state,
        referredByName,
        referredBy: referralCode || undefined,
      });

      trackGoogleAdsAccountCreatedVerifiedConversion();

      try {
        const storage = getPlatformStorage(undefined, 'local');

        await storage.removeItem('primum_referral_code');
      } catch {}

      toast.showSuccess({ title: 'Your NPI is verified', message: "You're all set." });

      redirectAfterVerification();
    } catch (error) {
      trackNpiVerificationFailed(error);

      if (isApiErrorV2(error)) {
        const title = error.body?.error || '';

        if (error.status === 409) {
          const body = error.body as {
            error?: string;
            errorCode?: 'NPI_IN_USE' | 'EMAIL_IN_USE' | string;
            resolution?: NpiConflictResolution;
          };

          const hasResolution = Boolean(
            body?.resolution?.duplicateUserId &&
            body?.resolution?.maskedEmail &&
            body?.resolution?.authProvider
          );

          if (hasResolution) {
            const authProvider = body.resolution!.authProvider === 'google' ? 'google' : 'password';

            conflictResolutionRef.current = {
              duplicateUserId: Number(body.resolution!.duplicateUserId),
              maskedEmail: String(body.resolution!.maskedEmail),
              authProvider,
            };

            trackAccountConflictDetected({
              conflictType: body.errorCode === 'EMAIL_IN_USE' ? 'EMAIL_IN_USE' : 'NPI_IN_USE',
              authProvider,
              signupMethod: isPendingOAuthSignup ? 'oauth' : 'password',
              source: 'npi_verification',
              userId,
              email: pendingUserData.email || currentUser?.email,
            });

            if (isPendingOAuthSignup) {
              setShowNpiConflict(true);

              return;
            }

            setError('npiNumber', { message: 'This NPI number is already in use' });

            return;
          }
        }

        if (title.includes('already associated')) {
          setError('npiNumber', { message: 'This NPI number is already in use' });

          toast.showError({
            title: 'Could not complete request',
            apiError: {
              error,
              errorPatternMatcher: {
                'This NPI Number is already associated with a user account':
                  'This NPI number is already in use',
              },
            },
          });

          return;
        }

        if (title.includes('Invalid NPI') || title.includes('Provider')) {
          setError('npiNumber', { message: 'This NPI number is invalid' });

          toast.showError({ title: 'This NPI number is invalid' });

          return;
        }
      }

      toast.showError({
        title: 'Could not complete request',
        apiError: {
          error,
          errorPatternMatcher: {},
        },
      });
    }
  });

  const startAccountLinking = async () => {
    const resolution = conflictResolutionRef.current;

    if (!resolution) {
      return;
    }

    try {
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken?.toString();

      const claims = idToken ? parseIdTokenClaims(idToken) : null;

      trackAccountLinkingInitiated({
        conflictType: 'NPI_IN_USE',
        source: 'npi_verification',
        authProvider: resolution.authProvider,
        userId,
        email: claims?.email,
      });

      if (!idToken) {
        toast.showError({ title: 'Could not prepare account linking. Please try again.' });

        return;
      }

      await saveAccountLinkingContext({
        duplicateUserId: resolution.duplicateUserId,
        duplicateIdToken: idToken,
        maskedEmail: resolution.maskedEmail,
        authProvider: resolution.authProvider,
        createdAt: Date.now(),
      });

      if (resolution.authProvider === 'password') {
        replace(appendRedirectPath(OPEN_LOGIN_PATH));

        await signOutLocal();

        return;
      }

      await signInWithGoogle({
        prompt: 'LOGIN',
        loginHint: claims?.email,
      });
    } catch {
      toast.showError({ title: 'Could not start account linking. Please try again.' });
    }
  };

  /**
   * Handles sign-in action from NPI conflict info box.
   * Initiates account linking flow to resolve NPI conflict.
   */
  const handleNpiConflictSignIn = async () => {
    await startAccountLinking();
  };

  /**
   * Handles contact support action from NPI conflict info box.
   * Opens support modal and tracks analytics event.
   */
  const handleNpiConflictContactSupport = () => {
    setShowSupportModal(true);

    trackSupportModalOpened({ step: 'step1', trigger: 'npi_error' });
  };

  if (!userId) {
    return null;
  }

  if (npiHeldForReview) {
    return (
      <VStack
        className="flex-1 px-4 web:md:px-0"
        style={{
          paddingBottom: bottom,
        }}>
        <KeyboardAwareScrollView
          mode="layout"
          bottomOffset={12}
          showsVerticalScrollIndicator={false}
          contentContainerClassName={
            inModal
              ? 'flex-col flex-grow items-stretch justify-start'
              : 'flex-col flex-grow items-center justify-center pt-10 pb-10 web:pb-12'
          }>
          {!inModal ? (
            <AwarenessLogo
              alt="Primum logo with mark"
              kind="dark2x"
              box={{ height: 35, width: 280, marginBottom: 8 }}
            />
          ) : null}

          <VStack className={inModal ? 'w-full px-2' : 'w-full max-w-[540px]'}>
            <NpiHeldForReviewCard onContactSupport={handleNpiConflictContactSupport} />
          </VStack>

          {isWeb ? (
            <VStack className="w-full max-w-[540px]">
              <Button
                size="xl"
                className="mt-3 w-full gap-x-2 web:mt-6"
                onPress={() => redirectAfterVerification()}>
                <ButtonText size="sm">Continue browsing</ButtonText>
              </Button>
            </VStack>
          ) : null}

          {!isWeb ? (
            <VStack className="w-full max-w-[540px]">
              <HStack className="mt-4 justify-center">
                <Link
                  onPress={() => {
                    signOut(undefined, 'npi_verification');
                  }}>
                  <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
                    Sign out
                  </LinkText>
                </Link>
              </HStack>
            </VStack>
          ) : null}
        </KeyboardAwareScrollView>

        {showSupportModal && (
          <SupportForm
            isOpen={showSupportModal}
            onClose={() => setShowSupportModal(false)}
            initialValues={{ email: currentUser?.email || '' }}
          />
        )}
      </VStack>
    );
  }

  return (
    <VStack
      className="flex-1 px-4 web:md:px-0"
      style={{
        paddingTop: isWeb || inModal ? 0 : top,
        paddingBottom: bottom,
      }}>
      <KeyboardAwareScrollView
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerClassName={
          inModal
            ? 'flex-col flex-grow items-stretch justify-start'
            : 'flex-col flex-grow items-center justify-center pb-10 pt-4 web:pb-12 web:pt-10'
        }>
        {!inModal ? (
          <AwarenessLogo
            alt="Primum logo with mark"
            kind="dark2x"
            box={{ height: 35, width: 280, marginBottom: 8 }}
          />
        ) : null}
        <VStack className="w-full max-w-[540px]">
          <VStack className={inModal ? 'px-2' : ''}>
            <VStack
              className="shadow-soft mt-3 items-start gap-4 self-stretch rounded-lg bg-background-0 px-6 pb-10 pt-6 web:mt-6 web:py-10"
              style={{ boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.1)' }}>
              <Text
                size="xl"
                className={
                  inModal
                    ? 'self-stretch text-left font-normal text-typography-900'
                    : 'self-stretch text-center font-normal text-typography-900'
                }>
                Verify Your Credentials
              </Text>
              <Text size="md" className="self-stretch text-left font-normal text-typography-900">
                <Text size="md" className="font-bold text-typography-900">
                  Primum is free for verified health care professionals in the United States.
                </Text>{' '}
                If you are eligible for access, please confirm your National Provider Identifier
                (NPI).
              </Text>

              <NpiSuggestionPicker
                enabled={suggestionsShown}
                referralCode={createAccountData?.referredBy || referredByParam || undefined}
                searchRequested={registrySearchRequested}
                onSelect={fillFromSuggestion}
              />

              {suggestionsShown ? (
                <HStack className="w-full items-center gap-3">
                  <Divider className="flex-1 bg-outline-200" />
                  <Text size="sm" className="text-typography-700">
                    or enter your NPI
                  </Text>
                  <Divider className="flex-1 bg-outline-200" />
                </HStack>
              ) : null}

              <Controller
                name="npiNumber"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl isInvalid={!!errors.npiNumber?.message} className="w-full">
                    <FormControlLabel className="mb-1.5">
                      <FormControlLabelText size="sm" className="font-[500] text-typography-900">
                        NPI*
                      </FormControlLabelText>
                    </FormControlLabel>

                    <Input className="w-full">
                      <InputField
                        className={twJoin(
                          'text-base',
                          errors.npiNumber?.message ? 'text-error-700' : 'text-typography-900'
                        )}
                        value={value}
                        placeholder="Enter your NPI"
                        maxLength={10}
                        onChangeText={(text: string) => onChange(text.replace(/\D/g, ''))}
                        keyboardType="number-pad"
                        inputAccessoryViewID={!isWeb ? 'npi-done' : undefined}
                        enterKeyHint="done"
                        returnKeyType="done"
                        blurOnSubmit
                        onSubmitEditing={() => {
                          if (isWeb) {
                            return;
                          }

                          Keyboard.dismiss();
                        }}
                      />
                      {providerStatusSlot}
                    </Input>

                    {!isWeb ? (
                      <InputAccessoryView nativeID="npi-done">
                        <HStack className="justify-end bg-background-0 px-3 py-2">
                          <Button size="sm" onPress={() => Keyboard.dismiss()}>
                            <ButtonText>Done</ButtonText>
                          </Button>
                        </HStack>
                      </InputAccessoryView>
                    ) : null}

                    {errors.npiNumber?.message && !showNpiConflict && (
                      <FormControlError className="mt-2 items-start">
                        <FormControlErrorIcon className="mt-0.5" size="xs" as={AlertCircleIcon} />
                        <FormControlErrorText size="xs">
                          {showSupportLinkForNpiError ? (
                            <>
                              {npiErrorMessage}. Need help?{' '}
                              <LinkText
                                onPress={() => {
                                  setShowSupportModal(true);

                                  trackSupportModalOpened({ step: 'step1', trigger: 'npi_error' });
                                }}
                                size="xs"
                                className="text-typography-error data-[hover=true]:text-typography-error">
                                Contact Support
                              </LinkText>
                            </>
                          ) : (
                            errors.npiNumber?.message
                          )}
                        </FormControlErrorText>
                      </FormControlError>
                    )}

                    {provider && isTenDigits && !errors.npiNumber?.message && (
                      <HStack className="mt-3 flex-wrap items-center gap-x-1">
                        <Text size="sm" className="text-typography-900">
                          {[`${provider.firstName} ${provider.lastName}`, provider.state]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                        <LinkText
                          onPress={clearNpi}
                          size="sm"
                          className="py-2 text-primary-500 data-[hover=true]:text-primary-600">
                          Not you?
                        </LinkText>
                      </HStack>
                    )}

                    {!showNpiConflict && !provider && (
                      <FormControlHelper className="mt-3">
                        <FormControlHelperText size="xs" className="text-typography-700">
                          Can't find your NPI?{' '}
                          {suggestionsShown ? null : (
                            <>
                              <LinkText
                                onPress={() => setRegistrySearchRequested(true)}
                                size="xs"
                                className="text-primary-500 data-[hover=true]:text-primary-600">
                                Search by name
                              </LinkText>
                              , or{' '}
                            </>
                          )}
                          <LinkText
                            onPress={() => {
                              trackNpiRegistryClicked({ suggestions_shown: suggestionsShown });

                              Linking.openURL(NPI_REGISTRY_URL).catch(() => {});
                            }}
                            size="xs"
                            className="text-primary-500 data-[hover=true]:text-primary-600">
                            {suggestionsShown ? 'Look it up' : 'look it up'} in the NPI registry
                          </LinkText>
                          .
                        </FormControlHelperText>
                      </FormControlHelper>
                    )}
                  </FormControl>
                )}
              />

              {/* NPI Conflict Info Box */}
              {showNpiConflict && conflictResolutionRef.current && (
                <NpiConflictInfoBox
                  maskedEmail={conflictResolutionRef.current.maskedEmail}
                  onSignIn={handleNpiConflictSignIn}
                  onContactSupport={handleNpiConflictContactSupport}
                />
              )}
            </VStack>
          </VStack>
        </VStack>

        <VStack className="w-full max-w-[540px]">
          <Button
            size="xl"
            className="mt-3 w-full gap-x-2 web:mt-6"
            onPress={handleNpiVerification}
            disabled={isSubmitting || isPending}>
            {isSubmitting || isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}
            <ButtonText size="sm">Continue</ButtonText>
          </Button>
          {isWeb && !isPendingOAuthSignup && (
            <Button
              variant="link"
              className="mt-2 w-full"
              onPress={() => {
                trackNpiVerificationSkipClicked();

                setShowSkipConfirm(true);
              }}>
              <ButtonText size="sm" className="text-typography-500">
                Skip for now
              </ButtonText>
            </Button>
          )}
        </VStack>
        <VStack className="w-full max-w-[540px]">
          <HStack className="mt-4 flex-wrap">
            <Text size="sm" className="text-typography-900">
              By proceeding, you agree to Primum's{' '}
            </Text>
            <Link isExternal href={TERMS_OF_USE_URL}>
              <LinkText
                size="sm"
                className="text-primary-500 underline data-[hover=true]:text-primary-600">
                Terms of Use
              </LinkText>
            </Link>
            <Text size="sm" className="text-typography-900">
              {' '}
              and{' '}
            </Text>
            <Link isExternal href={PRIVACY_POLICY_URL}>
              <LinkText
                size="sm"
                className="text-primary-500 underline data-[hover=true]:text-primary-600">
                Privacy Policy
              </LinkText>
            </Link>
          </HStack>
        </VStack>

        {!isWeb ? (
          <VStack className="w-full max-w-[540px]">
            <HStack className="mt-4 justify-center">
              <Link
                onPress={() => {
                  signOut(undefined, 'npi_verification');
                }}>
                <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
                  Sign out
                </LinkText>
              </Link>
            </HStack>
          </VStack>
        ) : null}
      </KeyboardAwareScrollView>

      {showSupportModal && (
        <SupportForm
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          initialValues={{ email: currentUser?.email || '' }}
        />
      )}
      <ConfirmModal
        isOpen={showSkipConfirm}
        title="Skip NPI verification?"
        description='Without an NPI, you&apos;ll have limited access to Primum. You can complete verification anytime by tapping "Complete Verification" in the navigation.'
        acceptText="Skip for now"
        cancelText="Go back"
        onConfirm={() => {
          trackNpiVerificationSkipConfirmed();

          setShowSkipConfirm(false);

          redirectAfterVerification();
        }}
        onCancel={() => {
          trackNpiVerificationSkipCancelled();

          setShowSkipConfirm(false);
        }}
      />
    </VStack>
  );
}
